"use client";

import { PROCESS_INSTANCE_QUERY } from "@/src/shared/const";
import { useSearchParams } from "next/navigation";
import { useFormContext } from "react-hook-form";
import { getContextVariablesToUpdate } from "./utils";
import {
  useNextNavigation,
  usePreviousNavigation,
  useSpecificStepNavigation
} from "@/src/components/Action/useNavigation";
import { useFormDataMiddleware } from "@/src/components/Action/useFormDataMiddleware";
import { useTimeSpentStore } from "@/src/stores/useTimeSpentStore";

export const useStepsNavigation = () => {
  const { getValues } = useFormContext();

  const searchParams = useSearchParams();

  const processInstanceId = searchParams.get(PROCESS_INSTANCE_QUERY);

  const { getTimeSpent } = useTimeSpentStore();
  const { formDataMiddleware } = useFormDataMiddleware();
  const { mutateAsync: mutateSpecificStep } = useSpecificStepNavigation();
  const { mutateAsync: mutateNextStep } = useNextNavigation();
  const { mutateAsync: mutatePreviousStep } = usePreviousNavigation();

  const {
    formState: { dirtyFields },
  } = useFormContext();

  const goToStep = async (
    code: string,
    navigationType: "NEXT" | "PREVIOUS" = "NEXT",
  ) => {
    if (!processInstanceId) {
      return;
    }

    const recordData = formDataMiddleware(null, getValues());
    return await mutateSpecificStep({
      code,
      body: {
        processInstanceId,
        contextVariablesToUpdate: getContextVariablesToUpdate(
          recordData,
          dirtyFields,
        ),
        timeSpent: getTimeSpent(),
        navigationType,
      },
    });
  };

  const goNext = async () => {
    if (!processInstanceId) {
      return;
    }

    const recordData = formDataMiddleware(null, getValues());
    const payload = {
      processInstanceId,
      contextVariablesToUpdate: getContextVariablesToUpdate(
        recordData,
        dirtyFields,
      ),
      timeSpent: getTimeSpent(),
    };
    return await mutateNextStep(payload);
  };

  const goBack = async () => {
    if (!processInstanceId) {
      return;
    }

    const recordData = formDataMiddleware(null, getValues());
    const payload = {
      processInstanceId,
      contextVariablesToUpdate: getContextVariablesToUpdate(
        recordData,
        dirtyFields,
      ),
      timeSpent: getTimeSpent(),
    };

    return await mutatePreviousStep(payload);
  };

  return { goNext, goToStep, goBack };
};
